import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Send, CheckCircle, AlertCircle, Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const container = useRef();
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: 'Proposal Development',
    message: '',
    budget: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useGSAP(() => {
    gsap.from('.contact-header', {
      opacity: 0,
      y: 40,
      duration: 0.8,
      scrollTrigger: {
        trigger: container.current,
        start: "top 70%",
      }
    });

    gsap.from('.contact-form', {
      opacity: 0,
      y: 50,
      duration: 0.8,
      scrollTrigger: {
        trigger: formRef.current,
        start: "top 75%",
      }
    });

    gsap.from('.contact-info-card', {
      opacity: 0,
      x: -30,
      duration: 0.8,
      stagger: 0.2,
      scrollTrigger: {
        trigger: '.contact-info-section',
        start: "top 75%",
      }
    });
  }, { scope: container });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, formData);
      
      setStatus({
        type: 'success',
        message: response.data.message || 'Thank you! We\'ll contact you within 24 hours to discuss your proposal needs.'
      });
      setIsSubmitting(false);
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        service: 'Proposal Development',
        message: '',
        budget: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to submit form. Please try again or email us directly at info@ironquillconsulting.com'
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={container} className="page-container">
      {/* Page Header */}
      <section className="page-header section-dark">
        <div className="section-content">
          <div className="contact-header" style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <div className="section-eyebrow">Get In Touch</div>
            <h1 style={{ fontSize: '48px', marginBottom: '24px' }}>Let's Discuss Your Proposal Needs</h1>
            <p style={{ fontSize: '20px', color: 'var(--text-secondary)' }}>
              Whether you need full lifecycle support or targeted assistance with an upcoming RFP, we're here to help you win.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section section-light">
        <div className="section-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'start' }}>
            {/* Contact Form */}
            <div ref={formRef}>
              <h2 style={{ marginBottom: '32px' }}>Send Us a Message</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label" htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="company">Company Name *</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="form-input"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label" htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-input"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john.smith@company.com"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="form-input"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder=""
                    />
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label" htmlFor="service">Service Needed *</label>
                    <select
                      id="service"
                      name="service"
                      className="form-input"
                      value={formData.service}
                      onChange={handleChange}
                      required
                    >
                      <option value="Proposal Development">Proposal Development</option>
                      <option value="Capture Support">Capture Support</option>
                      <option value="Proposal Management">Proposal Management</option>
                      <option value="Proposal Design">Proposal Design</option>
                      <option value="Multiple Services">Multiple Services</option>
                      <option value="Not Sure - Need Consultation">Not Sure - Need Consultation</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="budget">Budget Range</label>
                    <select
                      id="budget"
                      name="budget"
                      className="form-input"
                      value={formData.budget}
                      onChange={handleChange}
                    >
                      <option value="">Select Budget Range</option>
                      <option value="Under $25K">Under $25K</option>
                      <option value="$25K - $50K">$25K - $50K</option>
                      <option value="$50K - $100K">$50K - $100K</option>
                      <option value="$100K+">$100K+</option>
                      <option value="Prefer to Discuss">Prefer to Discuss</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="message">Project Details *</label>
                  <textarea
                    id="message"
                    name="message"
                    className="form-textarea"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Please describe your upcoming RFP, proposal challenges, timeline, and how we can support your team..."
                    style={{ minHeight: '140px' }}
                  />
                </div>

                {status.message && (
                  <div style={{
                    padding: '16px',
                    marginBottom: '20px',
                    background: status.type === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                    border: `1px solid ${status.type === 'success' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    color: status.type === 'success' ? '#4CAF50' : '#F44336'
                  }}>
                    {status.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                    <span>{status.message}</span>
                  </div>
                )}

                <button 
                  type="submit" 
                  className="form-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Message <Send size={20} style={{ marginLeft: '8px' }} />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="contact-info-section">
              <h2 style={{ marginBottom: '32px' }}>Contact Information</h2>
              
              <div className="contact-info-card" style={{
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border-subtle)',
                padding: '24px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                  <Mail size={24} style={{ color: 'var(--gold-primary)' }} />
                  <div>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Email</div>
                    <a href="mailto:info@ironquillconsulting.com" style={{ color: 'var(--text-primary)', textDecoration: 'none', fontSize: '18px' }}>
                      info@ironquillconsulting.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-info-card" style={{
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border-subtle)',
                padding: '24px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                  <Phone size={24} style={{ color: 'var(--gold-primary)' }} />
                  <div>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Phone</div>
                    <a href="tel:+15551234567" style={{ color: 'var(--text-primary)', textDecoration: 'none', fontSize: '18px' }}>
                      
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-info-card" style={{
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border-subtle)',
                padding: '24px',
                marginBottom: '32px'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <MapPin size={24} style={{ color: 'var(--gold-primary)', marginTop: '2px' }} />
                  <div>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Location</div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '18px' }}>
                      J&K, India
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                background: 'var(--gold-hover)',
                border: '1px solid var(--gold-primary)',
                padding: '24px',
              }}>
                <h3 style={{ fontSize: '20px', marginBottom: '12px', color: 'var(--text-primary)' }}>
                  Response Time
                </h3>
                <p style={{ fontSize: '15px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  We typically respond to all inquiries within 24 hours during business days.
                </p>
                <p style={{ fontSize: '14px', color: 'var(--gold-primary)', fontWeight: '600' }}>
                  For urgent RFP deadlines, call us directly for immediate assistance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Assessment CTA */}
      <section className="section section-dark">
        <div className="section-content">
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
            <h2 style={{ marginBottom: '24px' }}>Not Ready to Contact Us Yet?</h2>
            <p style={{ fontSize: '18px', color: 'var(--text-secondary)', marginBottom: '32px' }}>
              Download our free Proposal Readiness Checklist to assess your team's capabilities and identify gaps before reaching out.
            </p>
            <Link to="/free-assessment" className="btn-primary">
              Get Free Assessment
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
